import { type ReactNode, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { useSettingsStore } from '@shared/stores/settingsStore';
import { ToastViewport } from '@shared/ui/feedback/Toast';
import { useGuardTokenRefresh } from '@shared/hooks/useGuardTokenRefresh';
import { useGuardLifecycle } from '@shared/hooks/useGuardLifecycle';
import { installAlarmUnlocker } from '@shared/lib/alarm';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: true,
    },
    mutations: { retry: 0 },
  },
});

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps): JSX.Element {
  useEffect(() => {
    // One-shot listener that unlocks the AudioContext on first user gesture
    // so subsequent panic alarms aren't silently blocked by autoplay policy.
    installAlarmUnlocker();

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => useSettingsStore.getState().applyTheme();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <ErrorBoundary FallbackComponent={RootErrorFallback}>
      <QueryClientProvider client={queryClient}>
        {/* Lifecycle hooks must run INSIDE the QueryClientProvider — they
            call useQueryClient(). Calling them in `Providers` itself would
            run them above their own QueryClient context. */}
        <LifecycleEffects />
        {children}
        <ToastViewport />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

/** Runs the guard-side lifecycle hooks; mounted inside the QueryClientProvider. */
function LifecycleEffects(): null {
  useGuardTokenRefresh();
  useGuardLifecycle();
  return null;
}

function RootErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-white px-6 text-center text-ink">
      <h1 className="text-2xl font-semibold">Guard NOC error</h1>
      <pre className="max-w-full overflow-auto rounded bg-neutral-100 px-4 py-3 text-sm text-neutral-700">
        {error.message}
      </pre>
      <button
        type="button"
        onClick={resetErrorBoundary}
        className="rounded bg-red-600 px-5 py-2 text-sm font-medium text-white hover:bg-red-700"
      >
        Reload
      </button>
    </div>
  );
}

export { queryClient };
